// ============================================================================
// "Who are we bidding?" — design reference screen 2. The ONE component that
// writes a bid's start fields, for a new bid and for an edit (§15a).
// ⛔ Bryan, 2026-09-18, cut: due date, single-vs-package, drive time, scope
// grade, laundry. Do not add them back. HubSpot lookup is later — the deal id
// is a plain field.
// ============================================================================

import { useEffect, useState } from 'react';
import { Button } from '../../shared/components';
import { WriteDidNotLandError } from '../../shared/save-path';
import { errorMessage } from '../../shared/errors';
import { createBid, updateBid, FACILITY_TYPES, type BidRow, type Option } from './api';
import { Field, INPUT, SaveError } from './fields';
import { parseNumber } from './format';

export interface StartDraft {
  facility_name: string;
  prospect_city: string;
  prospect_state: string;
  market_region: string;
  facility_type: string;
  parent_id: string;
  account_id: string;
  decision_maker_name: string;
  decision_maker_email: string;
  incumbent_monthly_rate: string;
  walkthrough_notes: string;
  opportunity_id: string;
}

function draftFrom(b: BidRow | null): StartDraft {
  return {
    facility_name: b?.facility_name ?? '',
    prospect_city: b?.prospect_city ?? '',
    prospect_state: b?.prospect_state ?? '',
    market_region: b?.market_region ?? '',
    facility_type: b?.facility_type ?? '',
    parent_id: b?.parent_id ?? '',
    account_id: b?.account_id ?? '',
    decision_maker_name: b?.decision_maker_name ?? '',
    decision_maker_email: b?.decision_maker_email ?? '',
    incumbent_monthly_rate: b?.incumbent_monthly_rate != null ? String(b.incumbent_monthly_rate) : '',
    walkthrough_notes: b?.walkthrough_notes ?? '',
    opportunity_id: b?.opportunity_id ?? '',
  };
}

export function saveText(e: unknown): string {
  if (e instanceof WriteDidNotLandError) return e.described.message;
  return errorMessage(e, 'That did not save.');
}

export function StartForm({
  bid,
  markets,
  parents,
  accounts,
  onSaved,
  locked,
}: {
  bid: BidRow | null;
  markets: Option[];
  parents: Option[];
  accounts: Option[];
  onSaved: (b: BidRow) => void;
  locked?: boolean;
}) {
  const [d, setD] = useState<StartDraft>(() => draftFrom(bid));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setD(draftFrom(bid)), [bid?.bid_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof StartDraft) => (v: string) => setD((x) => ({ ...x, [k]: v }));
  const stateOk = d.prospect_state === '' || /^[A-Za-z]{2}$/.test(d.prospect_state);
  const payOk = d.incumbent_monthly_rate.trim() === '' || parseNumber(d.incumbent_monthly_rate) != null;
  const ready = d.facility_name.trim() !== '' && d.facility_type !== '' && d.prospect_state !== '' && stateOk && payOk;
  const why = !d.facility_name.trim()
    ? 'Name the prospect.'
    : !d.facility_type
      ? 'Pick the kind of building — it decides the questions that come next.'
      : !d.prospect_state || !stateOk
        ? 'Give the two-letter state — the comparables are matched by state.'
        : !payOk
          ? 'What they pay today must be a number (or blank).'
          : null;

  async function save() {
    setSaving(true);
    setErr(null);
    const fields = {
      facility_name: d.facility_name.trim(),
      prospect_city: d.prospect_city.trim() || null,
      prospect_state: d.prospect_state.trim().toUpperCase() || null,
      market_region: d.market_region || null,
      facility_type: d.facility_type,
      parent_id: d.parent_id || null,
      account_id: d.account_id || null,
      decision_maker_name: d.decision_maker_name.trim() || null,
      decision_maker_email: d.decision_maker_email.trim() || null,
      incumbent_monthly_rate: parseNumber(d.incumbent_monthly_rate),
      walkthrough_notes: d.walkthrough_notes.trim() || null,
      opportunity_id: d.opportunity_id.trim() || null,
    };
    try {
      const row = bid ? await updateBid(bid.bid_id, fields, 'save who we are bidding') : await createBid(fields);
      onSaved(row);
    } catch (e) {
      setErr(saveText(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <SaveError text={err} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Prospect">
          <input className={INPUT} value={d.facility_name} onChange={(e) => set('facility_name')(e.target.value)} disabled={locked} placeholder="Coastal Bend Surgery Center" />
        </Field>
        <Field label="HubSpot deal id (optional)" hint="Lookup from HubSpot is coming later — paste the deal id for now.">
          <input className={INPUT} value={d.opportunity_id} onChange={(e) => set('opportunity_id')(e.target.value)} disabled={locked} />
        </Field>
        <Field label="City">
          <input className={INPUT} value={d.prospect_city} onChange={(e) => set('prospect_city')(e.target.value)} disabled={locked} />
        </Field>
        <Field label="State" error={stateOk ? null : 'Two letters, e.g. TX'}>
          <input className={INPUT} value={d.prospect_state} maxLength={2} onChange={(e) => set('prospect_state')(e.target.value.toUpperCase())} disabled={locked} placeholder="TX" />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Kind of building">
          <select className={INPUT} value={d.facility_type} onChange={(e) => set('facility_type')(e.target.value)} disabled={locked}>
            <option value="">— pick —</option>
            {FACILITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Market">
          <select className={INPUT} value={d.market_region} onChange={(e) => set('market_region')(e.target.value)} disabled={locked}>
            <option value="">— pick —</option>
            {markets.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </Field>
        <Field label="Health system / brand (optional)">
          <select className={INPUT} value={d.parent_id} onChange={(e) => set('parent_id')(e.target.value)} disabled={locked}>
            <option value="">— none —</option>
            {parents.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Existing customer (optional)" hint="Only when a customer we already clean for is bidding another site.">
          <select className={INPUT} value={d.account_id} onChange={(e) => set('account_id')(e.target.value)} disabled={locked}>
            <option value="">— a new customer —</option>
            {accounts.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </Field>
        <Field label="What they pay today (if known)" hint="The anchor in every conversation — the app shows the step up automatically." error={payOk ? null : 'A number, e.g. 4000'}>
          <input className={INPUT} inputMode="decimal" value={d.incumbent_monthly_rate} onChange={(e) => set('incumbent_monthly_rate')(e.target.value)} disabled={locked} placeholder="$ a month" />
        </Field>
        <Field label="Decision maker">
          <input className={INPUT} value={d.decision_maker_name} onChange={(e) => set('decision_maker_name')(e.target.value)} disabled={locked} placeholder="Name, title" />
        </Field>
        <Field label="Decision maker's email">
          <input className={INPUT} type="email" value={d.decision_maker_email} onChange={(e) => set('decision_maker_email')(e.target.value)} disabled={locked} />
        </Field>
      </div>
      <Field label="Notes from the walkthrough">
        <textarea className={INPUT} rows={3} value={d.walkthrough_notes} onChange={(e) => set('walkthrough_notes')(e.target.value)} disabled={locked} />
      </Field>
      {!locked && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {why && <span className="text-xs text-tcs-text-muted">{why}</span>}
          <Button onClick={save} disabled={!ready} loading={saving}>
            {bid ? 'Save' : 'Continue to the building'}
          </Button>
        </div>
      )}
    </div>
  );
}
